import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { SocialSyncSchedule, SocialUniverseItem } from '@digital-twins/shared-types'
import type { SocialTopicListFacet, SocialTopicListFilters, SocialTopicListSort } from '@/types/components/social/topicListFilters'
import { filterTopicList, TOPIC_LIST_PARAMS, topicListFiltersFromParams, topicListHasFilters, topicListOptions } from './topicListFilters'

/** List-only state is namespaced; Resources/Audiences and unrelated query parameters are preserved. */
export function useSocialTopicListFilters(topics: ReadonlyArray<SocialUniverseItem>, schedule: SocialSyncSchedule) {
  const [params, setParams] = useSearchParams()
  const filters = useMemo(() => topicListFiltersFromParams(params), [params])
  const result = useMemo(() => filterTopicList(topics, filters, schedule), [topics, filters, schedule])
  const options = useMemo(() => topicListOptions(topics, filters, schedule), [topics, filters, schedule])
  const change = (key: keyof SocialTopicListFilters, value: string | null) => {
    setParams(previous => {
      const next = new URLSearchParams(previous)
      if (!value || (key === 'sort' && value === 'updated')) next.delete(TOPIC_LIST_PARAMS[key])
      else next.set(TOPIC_LIST_PARAMS[key], value)
      return next
    }, { replace: key === 'q' })
  }
  const clear = () => setParams(previous => {
    const next = new URLSearchParams(previous)
    Object.values(TOPIC_LIST_PARAMS).forEach(key => next.delete(key))
    return next
  })
  return {
    filters, options, result, hasFilters: topicListHasFilters(filters), clear,
    setFilter: (key: SocialTopicListFacet, value: string | null) => change(key, value),
    setSearch: (value: string) => change('q', value),
    setSort: (value: SocialTopicListSort) => change('sort', value),
  }
}
